import { EAddress } from './address';

export type MAIL_PROVIDER = 'lob' | 'manual';

/**
 * The type of document being mailed for a notice.
 * STANDARD_AFFIDAVIT is the legacy affidavit type.
 * RON_AFFIDAVIT and WET_SIG_AFFIDAVIT are explicit modern types whose
 * shipping billing units differ: wet sig is billed per address (consolidated
 * envelope), all others per document.
 */
export enum MailedNoticeDocumentType {
  STANDARD_AFFIDAVIT = 'standard_affidavit',
  INVOICE = 'invoice',
  RON_AFFIDAVIT = 'ron_affidavit',
  WET_SIG_AFFIDAVIT = 'wet_sig_affidavit'
}

export enum ShippingTier {
  GROUND = 'ground',
  EXPRESS = 'express',
  RUSH = 'rush'
}

/**
 * Determines how shipping for this delivery is billed.
 * Derived from documentType via getMailBillingUnit rather than stored directly.
 */
export enum MailBillingUnit {
  PER_ADDRESS = 'per_address',
  PER_DOCUMENT = 'per_document'
}

/**
 * Returns the billing unit for a given document type.
 * WET_SIG_AFFIDAVIT consolidates multiple docs into one envelope per address → PER_ADDRESS.
 * All other document types are billed per individual document → PER_DOCUMENT.
 */
export const getMailBillingUnit = (
  documentType: MailedNoticeDocumentType
): MailBillingUnit => {
  if (documentType === MailedNoticeDocumentType.WET_SIG_AFFIDAVIT) {
    return MailBillingUnit.PER_ADDRESS;
  }
  return MailBillingUnit.PER_DOCUMENT;
};

export type MailDelivery = {
  description: string;
  name: string;
  mailStatus?: number;
  copies?: number;
  address: EAddress;
  url?: string | null;
  expected_delivery_date?: string | null;
  isNoticeTypeDefault?: boolean;
  isCourthouse?: boolean;
  courtHouse?: number;
  provider?: MAIL_PROVIDER;
  documentType?: MailedNoticeDocumentType;
  shippingTier?: ShippingTier;
  // Track fulfillment to determine whether fee is refundable in case of cancellation
  fulfilled?: boolean;
};

export function isInvoiceMail(mail: MailDelivery): boolean {
  if (
    'documentType' in mail &&
    mail.documentType === MailedNoticeDocumentType.INVOICE
  ) {
    return true;
  }
  return false;
}
